import { forwardRef, type CSSProperties, type HTMLAttributes } from 'react'

import { KlimbThemes, type KlimbTheme } from './brand'
import { themeTokenStyle } from '../theme/themeTokens'

export type SurfaceMotif = 'none' | 'topo'

export interface SurfaceRootProps extends HTMLAttributes<HTMLDivElement> {
  theme?: KlimbTheme
  motif?: SurfaceMotif
  // brandTokens are a creator's vetted, server-sanitized brand overrides
  // (KAT-301). They are applied here — at the theme boundary — as CSS custom
  // properties via React's style prop, so values are set through the DOM style
  // API (never parsed as markup or a stylesheet). Unknown/invalid tokens are
  // dropped by themeTokenStyle.
  brandTokens?: Record<string, string> | null
}

// SurfaceRoot is the boundary that applies a Klimb theme to its subtree.
// It is a transparent wrapper: forwards refs and spreads any HTML attrs
// onto the underlying div so consumers can attach data-*, id, role, etc.
export const SurfaceRoot = forwardRef<HTMLDivElement, SurfaceRootProps>(function SurfaceRoot(
  { theme = 'klimb', motif = 'none', brandTokens, className, children, style, ...rest },
  ref,
) {
  const themeClass = KlimbThemes[theme]
  const classes = ['surface-root', themeClass, motif === 'topo' ? 'topo' : '', className]
    .filter(Boolean)
    .join(' ')

  // Merge the (already-safe) token custom properties over any caller style.
  // The token style object only ever contains CSS custom properties with
  // re-validated values, so it can't clobber the caller's own declarations.
  const mergedStyle: CSSProperties = { ...style, ...(themeTokenStyle(brandTokens) as CSSProperties) }

  return (
    <div ref={ref} className={classes} style={mergedStyle} {...rest}>
      {children}
    </div>
  )
})
